import { unwrapBackendConfigData } from "./configCrypto";

let _config = null;

// Always from .env — never overridden by backend API.
const ENV_ONLY_KEYS = new Set([
  "REACT_APP_BASE_URL",
  "REACT_APP_BASE_URL_BASE",
  "REACT_APP_BASE_URL_AI",
  "REACT_APP_GOOGLE_AUTH_URL",
]);

// Bootstrap / decryption only — not exposed as app config.
const ENV_META_KEYS = new Set([
  "REACT_APP_CONFIG_ENCRYPTION_KEY",
  "REACT_APP_BOOTSTRAP_URL",
]);

function _getReactEnvKeys() {
  return Object.keys(process.env).filter(
    (key) => key.startsWith("REACT_APP_") && !ENV_META_KEYS.has(key)
  );
}

function _toEnvKey(key) {
  return key.startsWith("REACT_APP_") ? key : `REACT_APP_${key}`;
}

function _toAlias(envKey) {
  return envKey.replace(/^REACT_APP_/, "");
}

function _collectConfigKeys(...sources) {
  const keys = new Set(_getReactEnvKeys());

  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const key of Object.keys(source)) {
      keys.add(_toEnvKey(key));
    }
  }

  return keys;
}

function _readConfigValue(source, envKey) {
  const alias = _toAlias(envKey);

  if (source[envKey] != null && source[envKey] !== "") return source[envKey];
  if (source[alias] != null && source[alias] !== "") return source[alias];

  return "";
}

function _normalizeConfig(raw = {}) {
  const merged = { ...process.env, ...raw };
  const config = {};

  for (const key of _collectConfigKeys(merged)) {
    config[key] = _readConfigValue(merged, key);
  }

  return config;
}

function _getBootstrapBaseUrl() {
  if (process.env.REACT_APP_BOOTSTRAP_URL) {
    return process.env.REACT_APP_BOOTSTRAP_URL.replace(/\/$/, "");
  }

  if (process.env.REACT_APP_BASE_URL_BASE) {
    return process.env.REACT_APP_BASE_URL_BASE.replace(/\/$/, "");
  }

  if (process.env.REACT_APP_BASE_URL) {
    return process.env.REACT_APP_BASE_URL.replace(/\/api\/v1\/?$/, "");
  }

  return "http://localhost:8000";
}

function _applyEnvOnlyKeys(config) {
  for (const key of ENV_ONLY_KEYS) {
    if (process.env[key]) {
      config[key] = process.env[key];
    }
  }
  return config;
}

async function _loadFromBackendApi() {
  const bootstrapBase = _getBootstrapBaseUrl();
  const configUrl = `${bootstrapBase}/api/v1/config/frontend`;

  const response = await fetch(configUrl);
  const json = await response.json();

  if (response.ok && json?.code === 200 && json?.data) {
    // console.log("Loaded from backend API:", configUrl);
    const rawData = await unwrapBackendConfigData(json.data);
    // console.log("[Config] Decrypted frontend config:", rawData);
    // console.table(
    //   Object.entries(rawData || {}).map(([key, value]) => ({
    //     key,
    //     value: String(value),
    //   }))
    // );
    return rawData;
  }

  throw new Error(json?.message || "Failed to load config from backend");
}

export async function loadSecrets() {
  if (_config) return _config;

  let apiConfig = {};
  let apiLoaded = false;

  try {
    apiConfig = await _loadFromBackendApi();
    apiLoaded = true;
  } catch (err) {
    console.error(
      `Could not load/decrypt backend config: ${err}. Continuing with .env values only.`
    );
  }

  // API/Secrets Manager wins over build-time process.env (except ENV_ONLY_KEYS below).
  _config = _applyEnvOnlyKeys(_normalizeConfig({ ...process.env, ...apiConfig }));

  const hasClientKey = Boolean(_config.REACT_APP_CLIENT_API_KEY);
  console.info(
    `[Config] backend=${apiLoaded ? "ok" : "failed"}, REACT_APP_CLIENT_API_KEY=${
      hasClientKey ? "present" : "MISSING"
    }`
  );

  return _config;
}

export function getConfig() {
  if (!_config) throw new Error("Secrets not loaded. Call loadSecrets() first.");
  return _config;
}
